import {
  OptimizationObjectiveType,
  OptimizationRunStatus,
} from '@warebee/frontend/data-access-api-graphql';
import classNames from 'classnames';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { formatTimespan } from '../../../common/formatHelper';
import useFormatter from '../../../common/useFormatter';
import InboxZero from '../../../components/InboxZero';
import LoadingIndicator from '../../../components/LoadingIndicator';
import { Button } from '../../../components/actions/Button';
import DropdownSelector from '../../../components/actions/DropdownSelector';
import * as Icon from '../../../components/icons';
import { Container } from '../../../components/layout/ContainerFlex';
import { ScreenTitle } from '../../../components/layout/ScreenTitle';
import { ActionBar } from '../../../components/nav/ActionBar';
import { Stat } from '../../../components/stats/Stat';
import { StatGroup } from '../../../components/stats/StatGroup';
import { loadBalancingPolicyDocument } from '../../../loadBalancingPolicy/loadBalancingPolicy.state';
import useStopOptimizationRun from '../../../warehouse/hooks/useStopOptimizationRun';
import { optimisationResult } from '../../store/optimisation.state';
import { simulationLayoutSummary } from '../../store/simulation.layout.state';
import { simulationCurrent } from '../../store/simulation.state';
import { getDimensionColor } from '../../store/workforce.helper';
import LineChart from '../LineChart';
import { StatMiniInline } from '../route/StatInline';
import OptimizationSettingsStats from './OptimisationSettingsInfo';
import OptimisationStatsInfo from './OptimisationStatsInfo';

const OptimisationProgressStatus: React.FC = () => {
  const { t } = useTranslation('simulation');
  const formatter = useFormatter();
  const simulation = useRecoilValue(simulationCurrent);
  const opt = useRecoilValue(optimisationResult);
  const layoutSummary = useRecoilValue(simulationLayoutSummary);
  const stopOptimization = useStopOptimizationRun();
  const [isLoading, setIsLoading] = useState(false);
  const [showStopOptions, setShowStopOptions] = useState<boolean>(false);
  const [showAdvancedStats, setShowAdvancedStats] = useState<boolean>(false);
  const [showImprovementRate, setShowImprovementRate] = useState(false);
  const [showSteps, setShowSteps] = useState(false);
  const [showMoves, setShowMoves] = useState(false);
  const balancingPolicy = useRecoilValue(loadBalancingPolicyDocument);

  const balancingPolicyRules = _.keyBy(balancingPolicy?.rules, r => r.id);

  const optStatus = opt?.status;
  const isStopping = optStatus === OptimizationRunStatus.STOPPING;
  const inProgress =
    optStatus === OptimizationRunStatus.CREATED ||
    optStatus === OptimizationRunStatus.IN_PROGRESS ||
    isStopping;

  const canStop =
    optStatus === OptimizationRunStatus.CREATED ||
    optStatus === OptimizationRunStatus.IN_PROGRESS;

  // console.log('opt?.progress', opt?.progress.toPrecision(2));
  // const optimisationProgress = Math.floor(Math.max(opt?.progress ?? 0, 1));
  const optimisationProgress =
    opt && typeof opt.progress === 'number'
      ? opt.progress < 1
        ? parseFloat(opt.progress.toFixed(1))
        : Math.floor(opt.progress)
      : 0;

  const showStats = !_.isNil(opt);

  const progressMessage =
    !isStopping && opt?.progress > 0
      ? t`Optimising`
      : isStopping
        ? t`Finalising Optimisation`
        : t`Preparing Data`;

  const statusRaw = opt?.status;
  const titleStatus =
    statusRaw === OptimizationRunStatus.COMPLETED
      ? t(`Optimised`)
      : statusRaw === OptimizationRunStatus.CREATED
        ? t(`Preparing Data`)
        : statusRaw === OptimizationRunStatus.FAILED
          ? t(`Error`)
          : statusRaw === OptimizationRunStatus.IN_PROGRESS
            ? t(`Optimising`)
            : statusRaw === OptimizationRunStatus.STOPPING
              ? t(`Finalising Optimisation`)
              : '—';

  const lastStep = _.last(opt?.progressDetails);
  const totalImprovementRaw = lastStep?.currentTotalImprovement ?? 0;
  const remainingTimeRaw = lastStep?.remainingComputeTime ?? 0;
  const elapsedTimeRaw = lastStep?.elapsedComputeTime ?? 0;
  const currentMoveCount = lastStep?.currentMoveCount ?? 0;
  const currentStepCount = lastStep?.step ?? 0;

  const totalImprovement = formatter.formatShare(totalImprovementRaw / 100);

  const objectivesTitles: Record<OptimizationObjectiveType, string> = {
    COST: t`Predicted Cost Improvement`,
    LOAD_BALANCING: t`Est. Balancing`,
  };

  function getTitle(
    type: OptimizationObjectiveType,
    ruleId: string,
    weight: number,
  ) {
    if (_.isNil(ruleId)) {
      return objectivesTitles[type];
    }
    return t('{{objType}} - {{ ruleTitle}}', {
      objType: objectivesTitles[type],
      ruleTitle: balancingPolicyRules[ruleId]?.title,
      ruleWeight: formatter.formatShare(weight).fullString,
    });
  }

  const costStats = _.filter(
    lastStep?.details?.improvement?.objectives,
    target => target.type === OptimizationObjectiveType.COST,
  );

  // If no cost stats in objectives, check for currentTotalImprovement
  if (
    costStats.length === 0 &&
    lastStep?.currentTotalImprovement !== undefined
  ) {
    costStats.push({
      type: OptimizationObjectiveType.COST,
      improvement: lastStep.currentTotalImprovement / 100, // Convert to ratio
      objectiveWeight: 1,
      initialCost: 0,
    } as any);
  }

  const loadBalancingStats = _.filter(
    lastStep?.details?.improvement?.objectives,
    target => target.type === OptimizationObjectiveType.LOAD_BALANCING,
  );

  // Centralized metric configuration
  const metricConfig = {
    COST: {
      key: 'COST',
      type: OptimizationObjectiveType.COST,
      label: t`Predicted Cost Improvement`,
      shortLabel: t`Improvement`,
      yAxis: 'left',
      color: getDimensionColor('COST'),
      format: (value: number) => {
        const formatted = formatter.formatShare(
          Math.abs(value) / 100,
        ).fullString;
        return value < 0 ? `-${formatted}` : formatted;
      },
      getValue: (data: any) => data.improvement * 100,
    },
    IMPROVEMENT_RATE: {
      key: 'IMPROVEMENT_RATE',
      type: 'IMPROVEMENT_RATE',
      label: t`Current Improvement Rate`,
      shortLabel: t`Improvement Rate`,
      yAxis: 'left',
      color: getDimensionColor('IMPROVEMENT_RATE'),
      format: (value: number) => {
        const formatted = formatter.formatShare(
          Math.abs(value) / 100,
        ).fullString;
        return value < 0 ? `-${formatted}` : formatted;
      },
    },
    IMPROVEMENT_PER_HOUR: {
      key: 'IMPROVEMENT_PER_HOUR',
      type: 'IMPROVEMENT_PER_HOUR',
      label: t`Average Improvement Per Step`,
      shortLabel: t`Avg/Step`,
      yAxis: 'left',
      color: getDimensionColor('IMPROVEMENT_PER_HOUR'),
      format: (value: number) => formatter.formatShare(value / 100).fullString,
    },
    STEPS: {
      key: 'STEPS',
      type: 'STEPS',
      label: t`Total Steps`,
      shortLabel: t`Steps`,
      yAxis: 'right',
      color: getDimensionColor('STEPS'),
      format: (value: number) =>
        formatter.formatInteger(Math.round(value)).fullString,
    },
    MOVES: {
      key: 'MOVES',
      type: 'MOVES',
      label: t`Total Moves`,
      shortLabel: t`Moves`,
      yAxis: 'right',
      color: getDimensionColor('MOVES'),
      format: (value: number) =>
        formatter.formatInteger(Math.round(value)).fullString,
    },
  };

  const [selectedMetrics, setSelectedMetrics] = React.useState<string[]>([
    'COST',
  ]);

  // 1. Prepare chart data with elapsed time as X
  const progressDetails = opt?.progressDetails || [];
  const seriesMap: Record<string, any> = {};
  let minTime = Infinity;
  let maxTime = -Infinity;

  progressDetails.forEach((stepDetails, idx) => {
    const time = stepDetails.elapsedComputeTime;
    minTime = Math.min(minTime, time);
    maxTime = Math.max(maxTime, time);

    // Legacy support for currentTotalImprovement
    if (_.isEmpty(stepDetails.details?.improvement?.objectives)) {
      if (!seriesMap['COST']) {
        seriesMap['COST'] = {
          id: metricConfig.COST.key,
          type: metricConfig.COST.type,
          title: metricConfig.COST.label,
          yAxis: metricConfig.COST.yAxis,
          data: [],
          config: metricConfig.COST,
        };
      }
      seriesMap['COST'].data.push({
        x: time,
        y: stepDetails.currentTotalImprovement,
        step: stepDetails.step,
      });
    }

    _.forEach(stepDetails.details?.improvement?.objectives, obj => {
      if (obj.type === OptimizationObjectiveType.LOAD_BALANCING && obj.id) {
        if (!seriesMap[obj.id]) {
          seriesMap[obj.id] = {
            id: obj.id,
            type: obj.type,
            title: getTitle(obj.type, obj.id, obj.objectiveWeight),
            yAxis: 'left',
            data: [],
          };
        }
        seriesMap[obj.id].data.push({
          x: time,
          y: obj.improvement * 100,
          step: stepDetails.step,
        });
      } else if (obj.type === OptimizationObjectiveType.COST) {
        if (!seriesMap['COST']) {
          seriesMap['COST'] = {
            id: metricConfig.COST.key,
            type: metricConfig.COST.type,
            title: metricConfig.COST.label,
            yAxis: metricConfig.COST.yAxis,
            data: [],
            config: metricConfig.COST,
          };
        }
        const existingPoint = seriesMap['COST'].data.find(d => d.x === time);
        if (!existingPoint) {
          seriesMap['COST'].data.push({
            x: time,
            y: obj.improvement * 100,
            step: stepDetails.step,
          });
        }
      }
    });
  });

  Object.values(seriesMap).forEach(serie => {
    serie.data = serie.data.map(d => ({
      x: d.x - minTime,
      y: d.y,
      step: d.step,
    }));
  });

  const improvementRateSerie = {
    id: metricConfig.IMPROVEMENT_RATE.key,
    type: metricConfig.IMPROVEMENT_RATE.type,
    title: metricConfig.IMPROVEMENT_RATE.label,
    yAxis: metricConfig.IMPROVEMENT_RATE.yAxis,
    data: [],
    config: metricConfig.IMPROVEMENT_RATE,
  };
  if (seriesMap['COST'] && seriesMap['COST'].data.length > 0) {
    let prevImprovement: number | undefined = undefined;
    seriesMap['COST'].data.forEach((point, idx) => {
      if (prevImprovement !== undefined) {
        improvementRateSerie.data.push({
          x: point.x,
          y: point.y - prevImprovement,
          step: point.step,
        });
      }
      prevImprovement = point.y;
    });
  }

  const improvementPerHourSerie = {
    id: metricConfig.IMPROVEMENT_PER_HOUR.key,
    type: metricConfig.IMPROVEMENT_PER_HOUR.type,
    title: metricConfig.IMPROVEMENT_PER_HOUR.label,
    yAxis: metricConfig.IMPROVEMENT_PER_HOUR.yAxis,
    data: [],
    config: metricConfig.IMPROVEMENT_PER_HOUR,
  };
  if (seriesMap['COST'] && seriesMap['COST'].data.length > 0) {
    seriesMap['COST'].data.forEach((point, idx) => {
      if (point.x > 0) {
        const avgPerHour = point.y / (point.x / 3600);
        improvementPerHourSerie.data.push({
          x: point.x,
          y: avgPerHour,
          step: point.step,
        });
      }
    });
  }

  const movesSerie = {
    id: metricConfig.MOVES.key,
    type: metricConfig.MOVES.type,
    title: metricConfig.MOVES.label,
    yAxis: metricConfig.MOVES.yAxis,
    data: progressDetails.map(d => ({
      x: d.elapsedComputeTime - minTime,
      y: d.currentMoveCount,
      step: d.step,
    })),
    config: metricConfig.MOVES,
  };

  // Build metricOptions including load balancing rules
  const loadBalancingRules = Object.entries(seriesMap)
    .filter(
      ([key, serie]) => serie.type === OptimizationObjectiveType.LOAD_BALANCING,
    )
    .map(([key, serie]) => ({
      key: key,
      label: serie.title,
      yAxis: 'left',
    }));

  const metricOptions = [
    {
      key: metricConfig.COST.key,
      label: metricConfig.COST.shortLabel,
      yAxis: metricConfig.COST.yAxis,
    },
    ...loadBalancingRules,
    {
      key: metricConfig.IMPROVEMENT_RATE.key,
      label: metricConfig.IMPROVEMENT_RATE.shortLabel,
      yAxis: metricConfig.IMPROVEMENT_RATE.yAxis,
    },
    {
      key: metricConfig.IMPROVEMENT_PER_HOUR.key,
      label: metricConfig.IMPROVEMENT_PER_HOUR.shortLabel,
      yAxis: metricConfig.IMPROVEMENT_PER_HOUR.yAxis,
    },
    {
      key: metricConfig.MOVES.key,
      label: metricConfig.MOVES.shortLabel,
      yAxis: metricConfig.MOVES.yAxis,
    },
  ];

  // Compose chart data based on selected metrics (remove steps as a series)
  const chartData = [];
  selectedMetrics.forEach(metric => {
    if (
      metric === 'COST' &&
      seriesMap['COST'] &&
      seriesMap['COST'].data.length > 0
    ) {
      const costSeries = {
        ...seriesMap['COST'],
        config: metricConfig.COST,
      };
      chartData.push(costSeries);
    } else if (
      metric === 'IMPROVEMENT_RATE' &&
      improvementRateSerie.data.length > 0
    ) {
      chartData.push(improvementRateSerie);
    } else if (
      metric === 'IMPROVEMENT_PER_HOUR' &&
      improvementPerHourSerie.data.length > 0
    ) {
      chartData.push(improvementPerHourSerie);
    } else if (metric === 'MOVES' && movesSerie.data.length > 0) {
      chartData.push(movesSerie);
    } else if (seriesMap[metric] && seriesMap[metric].data.length > 0) {
      chartData.push(seriesMap[metric]);
    }
  });

  const allTimes = chartData.flatMap(serie => serie?.data?.map(d => d.x) || []);
  const minTimeValue = 0;
  const maxTimeValue = maxTime - minTime;
  const midTimeValue = minTimeValue + (maxTimeValue - minTimeValue) / 2;
  const xTicks = [minTimeValue, midTimeValue, maxTimeValue];

  function formatSmartTime(tick: number) {
    if (maxTimeValue < 120) return `${Math.round(tick)}s`;
    if (maxTimeValue < 7200) return `${(tick / 60).toFixed(1)}m`;
    return `${(tick / 3600).toFixed(2)}h`;
  }

  // Check if any selected metrics have data
  const hasData = chartData.length > 0;
  const hasLeftAxisData = chartData.some(d => d.yAxis === 'left');
  const hasRightAxisData = chartData.some(d => d.yAxis === 'right');

  const selectedMetricsWithData = selectedMetrics.filter(metric => {
    if (metric === 'COST') return seriesMap['COST']?.data?.length > 0;
    if (metric === 'IMPROVEMENT_RATE')
      return improvementRateSerie.data.length > 0;
    if (metric === 'IMPROVEMENT_PER_HOUR')
      return improvementPerHourSerie.data.length > 0;
    if (seriesMap[metric]) return seriesMap[metric].data.length > 0;
    return false;
  });

  function formatSmartStep(tick: number) {
    return formatter.formatInteger(Math.round(tick)).value;
  }

  // Check if we have negative values in left axis data
  const hasNegativeValues = chartData
    .filter(d => d.yAxis === 'left')
    .some(serie => serie.data.some(point => point.y < 0));

  const yScales = hasRightAxisData
    ? {
        left: {
          type: 'linear',
          nice: true,
          min: hasNegativeValues ? 'auto' : 0,
          max: 'auto',
        },
        right: {
          type: 'linear',
          nice: true,
          min: 0,
          max: 'auto',
        },
      }
    : {
        left: {
          type: 'linear',
          nice: true,
          min: hasNegativeValues ? 'auto' : 0,
          max: 'auto',
        },
      };

  // Custom slice tooltip for x-axis slices
  const LineChartTooltip = React.useCallback(
    ({ slice }) => {
      if (!slice || !slice.points || slice.points.length === 0) {
        return null;
      }

      // Get x value from the first point
      const xValue = slice.points[0].data.x;
      const actualTime = xValue + minTime;
      // Find the closest entry in progressDetails for the given time
      const closest = progressDetails.reduce((prev, curr) =>
        Math.abs(curr.elapsedComputeTime - actualTime) <
        Math.abs(prev.elapsedComputeTime - actualTime)
          ? curr
          : prev,
      );
      const correspondingTime = closest?.elapsedComputeTime || 0;
      const correspondingStep = closest?.step || 0;
      const formattedTime = formatTimespan(correspondingTime * 1000);
      const formattedStep = formatter.formatInteger(
        Math.round(correspondingStep),
      ).value;

      // Create a map of points by serie ID to avoid duplicates
      const pointsMap = new Map();
      slice.points.forEach(point => {
        if (!pointsMap.has(point.serieId)) {
          pointsMap.set(point.serieId, {
            serieId: point.serieId,
            serie: chartData.find(s => s.id === point.serieId),
            data: point.data,
            color: point.color || getDimensionColor(point.serieId)[0],
          });
        }
      });
      const pointsAtX = Array.from(pointsMap.values());

      return (
        <div className="z-[9999]" style={{ pointerEvents: 'none' }}>
          <div className="min-w-tooltip bg-app-panel-dark/90 whitespace-nowrap p-2 text-sm backdrop-blur backdrop-filter">
            <div className="text-menu-active border-menu/50 mb-2 border-b pb-2 text-xs font-semibold">
              <StatMiniInline title={t`Step`} value={formattedStep} />
              <StatMiniInline title={t`Time`} value={formattedTime} />
            </div>

            {pointsAtX.map(pt => {
              // Get metric name and format value using config
              let metricName;
              let formattedValue;

              // Check if serie has a config (standard metrics)
              if (pt.serie.config && metricConfig[pt.serieId]) {
                metricName = pt.serie.config.label;
                formattedValue = pt.serie.config.format(pt.data.y);
              } else if (
                pt.serie.type === OptimizationObjectiveType.LOAD_BALANCING &&
                pt.serie.id
              ) {
                // For load balancing rules
                const rule = balancingPolicyRules[pt.serie.id];
                metricName = rule ? rule.title : pt.serie.title;
                formattedValue = formatter.formatShare(
                  pt.data.y / 100,
                ).fullString;
              } else {
                // Fallback
                metricName = pt.serie.title || pt.serieId;
                if (pt.serie.yAxis === 'left') {
                  formattedValue = formatter.formatShare(
                    pt.data.y / 100,
                  ).fullString;
                } else {
                  formattedValue = formatter.formatInteger(
                    Math.round(pt.data.y),
                  ).fullString;
                }
              }

              // Calculate the actual step for this data point
              const dataPointStep = pt.data.x + minTime;
              const dataPointStepFormatted = formatter.formatInteger(
                Math.round(dataPointStep),
              ).value;

              // Calculate corresponding time for this data point
              const dataPointTime =
                progressDetails.find(d => d.step === dataPointStep)
                  ?.elapsedComputeTime || 0;
              const dataPointTimeFormatted = formatTimespan(
                dataPointTime * 1000,
              );

              // Get color from config or use existing
              const color = pt.serie.config?.color?.[0] || pt.color;

              return (
                <div key={pt.serieId} className="py-2">
                  <div className="flex items-center gap-2">
                    <span
                      className="border-menu/50 h-3 w-3 rounded-full border"
                      style={{ backgroundColor: color }}
                    />
                    <span className="text-menu-text flex-1 text-xs">
                      {metricName}
                    </span>
                    <b className="text-menu-active text-sm">{formattedValue}</b>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );
    },
    [
      t,
      formatter,
      chartData,
      minTime,
      progressDetails,
      formatTimespan,
      balancingPolicyRules,
      OptimizationObjectiveType,
      metricConfig,
    ],
  );

  // Determine if any selected metric is a left-axis metric
  const hasLeftAxisMetric = selectedMetrics.some(
    key => metricOptions.find(opt => opt.key === key)?.yAxis === 'left',
  );
  // Determine if any selected metric is a right-axis metric
  const hasRightAxisMetric = selectedMetrics.some(
    key => metricOptions.find(opt => opt.key === key)?.yAxis === 'right',
  );

  const axisLeft = hasLeftAxisMetric
    ? {
        tickRotation: 0,
        legend: hasRightAxisMetric
          ? t`% Improvement / Rate`
          : selectedMetrics
              .map(m => metricOptions.find(o => o.key === m)?.label)
              .filter(Boolean)
              .join(' / '),
        legendOffset: -55,
        legendPosition: 'middle',
        format: v => {
          const leftMetricKey =
            selectedMetrics.find(
              key =>
                metricOptions.find(opt => opt.key === key)?.yAxis === 'left',
            ) || 'COST';
          return metricConfig[leftMetricKey]?.format
            ? metricConfig[leftMetricKey].format(v)
            : `${v}%`;
        },
      }
    : null;

  const axisRight = hasRightAxisMetric
    ? {
        tickRotation: 0,
        legend: t('Moves'),
        legendOffset: 50,
        legendPosition: 'middle',
        format: v => {
          // Format as integer with thousands separator
          return formatter.formatInteger(Math.round(v)).value;
        },
      }
    : null;

  const optimiseTime = formatTimespan(
    opt?.completedAt ?? 0,
    opt?.startedAt ?? 0,
  );

  // After seriesMap is built, determine if LOAD_BALANCING exists and update options/selection
  const hasLoadBalancingData = loadBalancingRules.length > 0;

  // On first data load, if LOAD_BALANCING exists and not already selected, add it
  useEffect(() => {
    if (hasLoadBalancingData && loadBalancingRules.length > 0) {
      setSelectedMetrics(prev => {
        const newMetrics = [...prev];
        loadBalancingRules.forEach(rule => {
          if (!newMetrics.includes(rule.key)) {
            newMetrics.push(rule.key);
          }
        });
        return newMetrics;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasLoadBalancingData]); // Remove loadBalancingRules from deps to avoid infinite loop

  // Generate dynamic title based on selected metrics
  const chartTitle = React.useMemo(() => {
    const selectedLabels = selectedMetrics
      .map(key => metricOptions.find(opt => opt.key === key)?.label)
      .filter(Boolean);

    if (selectedLabels.length === 0) return t`Select metrics to display`;
    if (selectedLabels.length === 1)
      return `${selectedLabels[0]} ${t`by time`}`;
    return `${selectedLabels.join(', ')} ${t`by time`}`;
  }, [selectedMetrics, metricOptions, t]);

  // const toggle = () => (
  //   <div className="border-menu-active/50 bg-sidebar-header/80 text-menu-active flex items-center rounded-md border px-2 py-1">
  //     <span>{t('Show Lines')}</span>
  //     <Icon.ChevronDown className="h-6 w-5 flex-shrink-0 fill-current" />
  //   </div>
  // );

  return (
    <Container col hasOverflowY>
      {/* // <ContainerScroll className="pb-20"> */}
      <ScreenTitle
        subtitle={t`Optimisation Status`}
        title={t`Optimisation`}
        helpNavTo={'simulation/optimise/simulation-optimise'}
        icon={Icon.SimulationOptimize}
      />
      {isLoading && (
        <LoadingIndicator message={t`Loading Data`} absolute selfCenter />
      )}

      {showStats && (
        <>
          <StatGroup
            title={t`Improvement Graph`}
            helpNavTo={'simulation/optimise/simulation-optimise'}
            titleAction={
              <div className="flex flex-row gap-2">
                {/* <Dropdown
                  toggle={toggle}
                  classNameDropDownMenu="w-48"
                  className="flex items-center"
                >
                  <div className="flex flex-col p-2">
                    {metricOptions.map(option => (
                      <div
                        key={option.key}
                        onClick={e => e.stopPropagation()}
                        className="p-1"
                      >
                        <InputCheckbox
                          isSelected={selectedMetrics.includes(option.key)}
                          onChange={() => {
                            setSelectedMetrics(prev => {
                              if (prev.includes(option.key)) {
                                return prev.filter(k => k !== option.key);
                              }
                              return [...prev, option.key];
                            });
                          }}
                        >
                          {option.label}
                        </InputCheckbox>
                      </div>
                    ))}
                  </div>
                </Dropdown> */}
                <DropdownSelector
                  classNameLabel="text-sm ltr:mr-1 rtl:ml-1"
                  onClick={e => e.stopPropagation()}
                  DropAlignRight
                  buttonTransparent
                  vertical
                  panelMode
                  value={
                    showAdvancedStats
                      ? t`Hide Support Options...`
                      : t`Show Support Options...`
                  }
                  values={[
                    showAdvancedStats
                      ? t`Hide Support Options...`
                      : t`Show Support Options...`,
                  ]}
                  onChange={async () =>
                    setShowAdvancedStats(!showAdvancedStats)
                  }
                />
              </div>
            }
          >
            <div
              className={classNames(
                'flex flex-1 flex-col items-center',
                'relative',
                'bg-app-panel-dark/60',
                'h-fill w-full',
              )}
            >
              {isLoading && (
                <LoadingIndicator
                  message={t`Loading Data`}
                  absolute
                  selfCenter
                />
              )}
              <label
                aria-label="Title"
                className="text-xxs w-full flex-1 uppercase xl:text-sm"
              >
                <div className="flex items-center pt-4">
                  <span className="flex-1 ltr:ml-4 rtl:mr-4">{chartTitle}</span>
                </div>
              </label>

              {inProgress && (
                <div className="flex w-full">
                  <LoadingIndicator
                    className="flex-1"
                    progress={optimisationProgress}
                    message={titleStatus}
                    selfCenter
                    transparent
                    oneLiner
                    circleSize={64}
                    circleStroke={4}
                  />
                  <DropdownSelector
                    classNameLabel="text-sm"
                    onClick={e => {
                      e.stopPropagation();
                    }}
                    DropAlignRight
                    buttonTransparent
                    vertical
                    panelMode
                    value={'...'}
                    values={[
                      showStopOptions
                        ? t`Hide Options...`
                        : t`Show Run Options`,
                    ]}
                    onChange={async (option, e) => {
                      setShowStopOptions(!showStopOptions);
                    }}
                  />
                </div>
              )}

              {showStopOptions && canStop && (
                <div
                  className={classNames(
                    'p-4',
                    'w-full',
                    'bg-issue-transparent bg-app-panel-dark/75',
                    'transform transition-all',
                  )}
                >
                  <ActionBar className="my-2">
                    <Button
                      full
                      className="truncate rounded text-xs"
                      label={t`Stop Optimisation Run`}
                      buttonType="delete"
                      buttonSize="xs"
                      isDisabled={isLoading}
                      onPress={async () => {
                        setIsLoading(true);
                        await stopOptimization(opt.id);
                        setIsLoading(false);
                      }}
                      hasIconAfter={
                        <Icon.CircleX
                          className={classNames('h-6 w-6 fill-current')}
                        />
                      }
                    />
                  </ActionBar>
                </div>
              )}

              <div
                className={classNames(
                  'flex flex-col',
                  'relative',
                  'px-1 py-0.5 md:px-2 lg:px-3 xl:px-4 ltr:px-0.5 ltr:xl:px-1 rtl:px-0.5 rtl:xl:px-1',
                  'm-0.5',
                )}
                style={{
                  height: 340,
                  width: '100%',
                  minWidth: '100%',
                }}
              >
                {isLoading && (
                  <LoadingIndicator
                    message={t`Loading Chart Data`}
                    absolute
                    selfCenter
                  />
                )}
                {/* {chartData.length === 0 ? (
                  <InboxZero
                    message={
                      selectedMetrics.length === 0
                        ? t`No metrics selected`
                        : selectedMetricsWithData.length === 0
                          ? t`No data available for selected metrics. If you expect data, please check your optimization run or settings.`
                          : t`No data to display`
                    }
                    selfCenter
                  />
                ) : ( */}
                <div className="relative flex h-full w-full overflow-hidden">
                  {chartData.length === 0 && (
                    <InboxZero
                      message={
                        isLoading || !elapsedTimeRaw
                          ? t`Loading data...`
                          : selectedMetrics.length === 0
                            ? t`No metrics selected`
                            : selectedMetricsWithData.length === 0
                              ? t`No data available for selected metrics. If you expect data, please check your optimization run or settings.`
                              : t`No data to display`
                      }
                      selfCenter
                      className="!absolute bottom-0 left-0 right-0 top-0"
                    />
                  )}
                  <LineChart
                    data={chartData}
                    xTicks={xTicks}
                    xTickFormat={formatSmartTime}
                    yScales={yScales}
                    sliceTooltip={LineChartTooltip}
                    axisLeft={axisLeft}
                    axisRight={axisRight}
                  />
                </div>
                {/* )} */}
                {/* Time stats */}
                {elapsedTimeRaw > 0 && (
                  <StatMiniInline
                    // legendColor={getDimensionColor('TIME')}
                    title={t`Elapsed Time`}
                    titleAlt={t`Total time elapsed since optimization started`}
                    value={formatTimespan(elapsedTimeRaw * 1000)}
                    // hasHelper
                    // inPanelMode
                    // isFullWidth
                    // isPreview
                  />
                )}
              </div>

              {inProgress && (
                <footer className="h-auto w-full px-0.5 pb-4 pt-2 lg:px-2">
                  <div
                    className={classNames(
                      'text-menu-active flex-1 px-2 text-xs xl:px-4',
                    )}
                  >
                    <span
                      className={classNames(
                        'text-xxs text-menu-text/60 uppercase ltr:mr-2 rtl:ml-2',
                      )}
                    >{`Elapsed Time`}</span>
                    <span>
                      {elapsedTimeRaw
                        ? formatTimespan(elapsedTimeRaw * 1000)
                        : t`Calculating...`}
                    </span>
                  </div>

                  <div
                    className={classNames(
                      'text-menu-active flex-1 px-2 text-xs xl:px-4',
                    )}
                  >
                    <span
                      className={classNames(
                        'text-xxs text-menu-text/60 uppercase ltr:mr-2 rtl:ml-2',
                      )}
                    >{`Est. Remaining Time`}</span>
                    <span>
                      {remainingTimeRaw
                        ? formatTimespan(remainingTimeRaw * 1000)
                        : t`Calculating...`}
                    </span>
                  </div>
                </footer>
              )}
            </div>

            <StatGroup
              title={t`Improvement Stats`}
              helpNavTo={'simulation/optimise/simulation-optimise'}
            >
              {isLoading || !elapsedTimeRaw ? (
                <div className="flex">
                  <LoadingIndicator
                    message={t`Loading stats`}
                    // absolute
                    selfCenter
                    oneLiner
                    // className="w-full"
                  />
                </div>
              ) : (
                <div className="flex flex-col gap-0.5">
                  {/* Helper function to render metric stats */}
                  {(() => {
                    const renderMetricStat = (
                      key: string,
                      config: any,
                      value: number,
                      isAvailable: boolean = true,
                      isPrimary: boolean = false,
                      isPositive: boolean = true,
                    ) => {
                      if (!isAvailable) return null;
                      const isSelected = selectedMetrics.includes(key);
                      const formattedValue = config.format(value);

                      return (
                        <Stat
                          key={key}
                          legendColor={config.color}
                          title={config.label}
                          titleAlt={config.label}
                          value={formattedValue}
                          hasHelper
                          isPrimaryResult={isPrimary}
                          inPanelMode
                          isFullWidth
                          isPreview
                          isActionable
                          isSelected={isSelected}
                          className={!isPositive ? 'text-issue' : ''}
                          onClick={() => {
                            setSelectedMetrics(prev => {
                              if (prev.includes(key)) {
                                return prev.filter(k => k !== key);
                              }
                              return [...prev, key];
                            });
                          }}
                        />
                      );
                    };

                    // Cost Improvement Stats
                    const costStat = costStats.length > 0 ? costStats[0] : null;
                    const metrics = [
                      {
                        key: metricConfig.COST.key,
                        config: metricConfig.COST,
                        value: costStat ? costStat.improvement * 100 : 0,
                        isAvailable: costStat !== null,
                        isPrimary: true,
                        isPositive: costStat ? costStat.improvement >= 0 : true,
                      },
                      {
                        key: metricConfig.IMPROVEMENT_RATE.key,
                        config: metricConfig.IMPROVEMENT_RATE,
                        value: _.last(improvementRateSerie.data)?.y ?? 0,
                        isAvailable:
                          improvementRateSerie.data.length > 0 &&
                          (_.last(improvementRateSerie.data)?.y ?? 0) !== 0,
                        isPositive:
                          (_.last(improvementRateSerie.data)?.y ?? 0) >= 0,
                      },
                      {
                        key: metricConfig.IMPROVEMENT_PER_HOUR.key,
                        config: metricConfig.IMPROVEMENT_PER_HOUR,
                        value: _.last(improvementPerHourSerie.data)?.y ?? 0,
                        isAvailable: improvementPerHourSerie.data.length > 0,
                        isPositive:
                          (_.last(improvementPerHourSerie.data)?.y ?? 0) >= 0,
                      },
                      {
                        key: metricConfig.MOVES.key,
                        config: metricConfig.MOVES,
                        value: currentMoveCount,
                        isAvailable: true,
                        isPositive: true,
                      },
                    ];

                    return (
                      <>
                        {metrics.map(metric =>
                          renderMetricStat(
                            metric.key,
                            metric.config,
                            metric.value,
                            metric.isAvailable,
                            metric.isPrimary,
                            metric.isPositive,
                          ),
                        )}
                      </>
                    );
                  })()}

                  {/* {remainingTimeRaw > 0 && (
                    <Stat
                      legendColor={getDimensionColor('TIME')}
                      title={t`Estimated Remaining Time`}
                      titleAlt={t`Estimated time until optimization completes`}
                      value={formatTimespan(remainingTimeRaw * 1000)}
                      hasHelper
                      inPanelMode
                      isFullWidth
                      isPreview
                    />
                  )} */}
                </div>
              )}
            </StatGroup>
          </StatGroup>
          {loadBalancingStats.length > 0 && (
            <StatGroup
              title={t`Workload Balancing`}
              helpNavTo={'simulation/optimise/simulation-optimise'}
            >
              {/* Render LOAD_BALANCING stats */}
              <div
                className={`flex ${
                  loadBalancingStats.length > 3
                    ? 'flex-col space-y-1'
                    : 'flex-col space-y-1 xl:flex-row xl:space-x-1 xl:space-y-0'
                }`}
              >
                {_.map(loadBalancingStats, target => {
                  const isSelected = selectedMetrics.includes(target.id);
                  return (
                    <Stat
                      key={target.id}
                      legendColor={getDimensionColor(target.id ?? target.type)}
                      title={getTitle(
                        target.type,
                        target.id,
                        target.objectiveWeight,
                      )}
                      titleClassName="text-xxs"
                      titleAlt={getTitle(
                        target.type,
                        target.id,
                        target.objectiveWeight,
                      )}
                      value={
                        formatter.formatShare(target.improvement).fullString
                      }
                      hasHelper
                      // isPrimaryResult={isPrimary}
                      inPanelMode
                      isFullWidth
                      isPreview
                      isActionable
                      isSelected={isSelected}
                      onClick={() => {
                        const metricKey = target.id;
                        setSelectedMetrics(prev => {
                          if (prev.includes(metricKey)) {
                            return prev.filter(k => k !== metricKey);
                          }
                          return [...prev, metricKey];
                        });
                      }}
                    />
                  );
                })}
              </div>
            </StatGroup>
          )}

          {showAdvancedStats && (
            <>
              <OptimisationStatsInfo opt={opt} layoutSummary={layoutSummary} />
              <OptimizationSettingsStats
                optimizationSettings={simulation?.optimizationSettings}
              />
            </>
          )}
        </>
      )}
    </Container>
  );
};

export default OptimisationProgressStatus;
